import type { EntityTableAdapter } from './EntityTableAdapter';
import type { TableColumn } from '../GenericTable';
import type { ScimEntityService } from '../../../services/ScimEntityService';

export interface LocalitySearchParams {
    filter?: string;
    page: number;
    pageSize: number;
    sortBy?: string;
    searchtext?: string;
    /** Comma-separated attribute names forwarded as `searchAttributes` on the SCIM list request. */
    searchAttributes?: string;
    omitSearchDialectExpression?: boolean;
    abortSignal?: AbortSignal;
}

export type LocalitySearchFn = (params: LocalitySearchParams) => Promise<any>;

/**
 * Shared adapter for Building / School / Class / ResponsibilityCenter list screens
 * (locality SCIM services + `DynamicTableView`).
 */
export function createLocalityManagementTableAdapter<T>(args: {
    /** `DynamicTableView` entityType (e.g. `"Building"`). */
    entityType: string;
    /** First argument to `ScimEntityService.deleteEntity` (e.g. `"Building"`). */
    deleteEntityType: string;
    authResolved: boolean;
    itemsPerPage: number;
    entityService: ScimEntityService;
    search: LocalitySearchFn;
    getColumns: () => TableColumn<T>[];
}): EntityTableAdapter<T> {
    const { entityType, deleteEntityType, authResolved, itemsPerPage, entityService, search, getColumns } = args;

    return {
        entityType,
        getColumns,
        loadData: async (
            page,
            abortSignal,
            searchFilter,
            _selectedType,
            sortBy,
            searchtext,
            searchAttributes,
            omitSearchDialectExpression
        ) => {
            if (!authResolved) {
                return { data: [], totalResults: 0, currentPage: 1, totalPages: 1, hasNextPage: false, hasPreviousPage: false };
            }
            const response = await search({
                filter: searchFilter,
                page,
                pageSize: itemsPerPage,
                sortBy,
                searchtext,
                searchAttributes,
                omitSearchDialectExpression,
                abortSignal
            });
            const rows = (response.localities ?? response.items ?? []) as any[];
            return {
                ...response,
                data: rows,
                items: rows,
                totalResults: Number(response.totalResults) || 0,
                currentPage: page,
                totalPages: Number(response.totalPages) || 1,
                hasNextPage: (response.hasNextPage as boolean | undefined) ?? page < (Number(response.totalPages) || 1),
                hasPreviousPage: (response.hasPreviousPage as boolean | undefined) ?? page > 1
            };
        },
        deleteById: async (id: string) => {
            await entityService.deleteEntity(deleteEntityType, id);
        }
    };
}
